import { PedidoCard, KanbanComprasData, OrderItem } from "@/services/suprimentos";

/* ─── Tipos dos dados dos gráficos ──────────────────────────────────────── */

export interface MonthStat {
  mes:         string;      // "Jan/25"
  mesKey:      string;      // "2025-01"
  quantidade:  number;
  valor:       number;
  ticketMedio: number;
  pedidos:     PedidoCard[];
}

export interface CentroCustoStat {
  nome:       string;
  nomeAbrev:  string;
  quantidade: number;
  valor:      number;
  pedidos:    PedidoCard[];
}

export interface FornecedorStat {
  nome:        string;
  nomeAbrev:   string;
  quantidade:  number;
  valor:       number;
  ticketMedio: number;
  pedidos:     PedidoCard[];
}

export interface StatusStat {
  name:       string;
  value:      number;
  quantidade: number;
  fill:       string;
  pedidos:    PedidoCard[];
}

export interface InsumoStat {
  /** Label normalizado (sem prefixo "N. ") */
  nome:        string;
  nomeAbrev:   string;
  /** Nº de pedidos / ocorrências */
  quantidade:  number;
  /** Valor total comprado (apenas quando vem de OrderItem) */
  valorTotal?: number;
  /** Quantidade total comprada (apenas quando vem de OrderItem) */
  qtdComprada?: number;
  /** Pedidos que contêm este insumo (apenas quando vem de itemsPreview) */
  pedidos:     PedidoCard[];
  /** IDs dos pedidos */
  orderIds:    string[];
  /** Itens brutos (apenas quando vem da API de items) */
  rawItems?:   OrderItem[];
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const MONTHS_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function mesLabel(key: string): string {
  const [y, m] = key.split("-");
  return `${MONTHS_PT[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

function abrev(name: string, max = 22): string {
  return name.length > max ? name.slice(0, max - 1) + "…" : name;
}

/** Remove prefixo "N. " dos labels de itens vindos do backend. */
function normalizeItemLabel(s: string): string {
  return s.replace(/^\d+\.\s*/, "").trim();
}

export function getAllPedidos(data: KanbanComprasData): PedidoCard[] {
  return [...data.pedidosSemFinanceiro, ...data.pedidosGeradoFinanceiro];
}

/* ─── 1. Pedidos por Mês ─────────────────────────────────────────────────── */

export function computePedidosPorMes(pedidos: PedidoCard[]): MonthStat[] {
  const map = new Map<string, PedidoCard[]>();
  for (const p of pedidos) {
    const key = p.date?.substring(0, 7) ?? "0000-00";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, ps]) => {
      const valor = ps.reduce((s, p) => s + (p.value ?? 0), 0);
      return {
        mes:         mesLabel(key),
        mesKey:      key,
        quantidade:  ps.length,
        valor,
        ticketMedio: ps.length > 0 ? valor / ps.length : 0,
        pedidos:     ps,
      };
    });
}

/* ─── 2. Pedidos por Centro de Custo ─────────────────────────────────────── */

/**
 * @param ccNameMap Mapa id → nome do centro de custo (opcional).
 *   Quando costCenterName do pedido estiver vazio, o id é usado como fallback para buscar o nome.
 */
export function computePedidosPorCentro(
  pedidos: PedidoCard[],
  ccNameMap?: Map<string, string>
): CentroCustoStat[] {
  const map = new Map<string, PedidoCard[]>();
  for (const p of pedidos) {
    const resolved =
      p.costCenterName?.trim() ||
      (p.costCenterId ? (ccNameMap?.get(p.costCenterId) ?? "") : "") ||
      "Não informado";
    if (!map.has(resolved)) map.set(resolved, []);
    map.get(resolved)!.push(p);
  }
  return Array.from(map.entries())
    .map(([nome, ps]) => {
      const valor = ps.reduce((s, p) => s + (p.value ?? 0), 0);
      return { nome, nomeAbrev: abrev(nome), quantidade: ps.length, valor, pedidos: ps };
    })
    .sort((a, b) => b.valor - a.valor);
}

/* ─── 3. Status dos Pedidos ─────────────────────────────────────────────── */

export function computeStatusPedidos(data: KanbanComprasData): StatusStat[] {
  const semFin     = data.pedidosSemFinanceiro;
  const comFin     = data.pedidosGeradoFinanceiro;
  const recebidos  = comFin.filter((p) => p.received);
  const aguardando = comFin.filter((p) => !p.received);

  return [
    { name: "Pendente",     value: semFin.length,     quantidade: semFin.length,     fill: "#ef4444", pedidos: semFin     },
    { name: "Ag. Entrega",  value: aguardando.length, quantidade: aguardando.length, fill: "#f59e0b", pedidos: aguardando },
    { name: "Recebido",     value: recebidos.length,  quantidade: recebidos.length,  fill: "#10b981", pedidos: recebidos  },
  ].filter((s) => s.quantidade > 0);
}

/* ─── 4. Evolução de Valores por Mês ─────────────────────────────────────── */

export function computeEvolucaoPrecos(pedidos: PedidoCard[]): MonthStat[] {
  return computePedidosPorMes(pedidos);
}

/* ─── 5. Ranking de Fornecedores ─────────────────────────────────────────── */

/** Ranking completo de fornecedores do período (sem limite de quantidade). */
export function computeRankingFornecedores(pedidos: PedidoCard[]): FornecedorStat[] {
  const map = new Map<string, PedidoCard[]>();
  for (const p of pedidos) {
    const key = p.supplier?.trim() || "Não informado";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  return Array.from(map.entries())
    .map(([nome, ps]) => {
      const valor = ps.reduce((s, p) => s + (p.value ?? 0), 0);
      return { nome, nomeAbrev: abrev(nome, 28), quantidade: ps.length, valor, ticketMedio: ps.length > 0 ? valor / ps.length : 0, pedidos: ps };
    })
    .sort((a, b) => b.valor - a.valor);
}

/* ─── 6. Pedidos por Valor (top N) ──────────────────────────────────────── */

export interface PedidoValorStat {
  pedido:    PedidoCard;
  label:     string;   // "#1234 – Fornecedor"
  valor:     number;
}

export function computePedidosPorValor(pedidos: PedidoCard[], top = 20): PedidoValorStat[] {
  return [...pedidos]
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    .slice(0, top)
    .map((p) => ({
      pedido: p,
      label:  `#${p.orderNumber ?? p.purchaseOrderId}`,
      valor:  p.value ?? 0,
    }));
}

/* ─── 7. Maiores Aumentos de Preço por Insumo ────────────────────────────── */

export interface AumentoInsumoStat {
  nome:         string;
  nomeAbrev:    string;
  /** % variação do 1º ao último mês disponível */
  pctAumento:   number;
  precoInicial: number;
  precoFinal:   number;
  rawItems:     OrderItem[];
  orderIds:     string[];
  /** Série mensal para o gráfico de evolução % */
  evolucao:     { mes: string; mesKey: string; precoMedio: number; pct: number }[];
}

export function computeAumentosPreco(items: OrderItem[], top = 15): AumentoInsumoStat[] {
  const byDesc = new Map<string, OrderItem[]>();
  for (const it of items) {
    const nome = normalizeItemLabel(it.description || it.code || "");
    if (!nome) continue;
    if (!byDesc.has(nome)) byDesc.set(nome, []);
    byDesc.get(nome)!.push(it);
  }

  const results: AumentoInsumoStat[] = [];

  for (const [nome, its] of byDesc) {
    // ── 1. Agrupa por data DIÁRIA (YYYY-MM-DD) para detectar variação
    //       mesmo dentro de um único mês de filtro.
    const byDay = new Map<string, { total: number; count: number }>();
    for (const it of its) {
      const raw = (it.orderDate ?? "").substring(0, 10);
      if (raw.length < 10) continue;
      const price = it.netPrice > 0 ? it.netPrice : it.unitPrice;
      if (price <= 0) continue;
      if (!byDay.has(raw)) byDay.set(raw, { total: 0, count: 0 });
      byDay.get(raw)!.total += price;
      byDay.get(raw)!.count += 1;
    }

    const days = Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, acc]) => ({ key, precoMedio: acc.total / acc.count }));

    // Precisa de pelo menos 2 datas distintas com preços diferentes
    if (days.length < 2) continue;

    const precoInicial = days[0].precoMedio;
    const precoFinal   = days[days.length - 1].precoMedio;
    if (precoInicial === 0) continue;
    const pctAumento = ((precoFinal - precoInicial) / precoInicial) * 100;
    if (pctAumento <= 0) continue;

    // ── 2. Série para o gráfico de evolução:
    //    - Se há múltiplos meses → agrupa por mês (label "Abr/26")
    //    - Se tudo no mesmo mês  → exibe datas individuais (label "05/04")
    const monthKeys = [...new Set(days.map((d) => d.key.substring(0, 7)))];
    const multiMonth = monthKeys.length > 1;

    let evolucao: { mes: string; mesKey: string; precoMedio: number; pct: number }[];

    if (multiMonth) {
      const byMonth = new Map<string, { total: number; count: number }>();
      for (const { key, precoMedio } of days) {
        const mk = key.substring(0, 7);
        if (!byMonth.has(mk)) byMonth.set(mk, { total: 0, count: 0 });
        byMonth.get(mk)!.total += precoMedio;
        byMonth.get(mk)!.count += 1;
      }
      evolucao = Array.from(byMonth.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mk, acc]) => {
          const pm = acc.total / acc.count;
          return {
            mes:       mesLabel(mk),
            mesKey:    mk,
            precoMedio: pm,
            pct:       ((pm - precoInicial) / precoInicial) * 100,
          };
        });
    } else {
      // Mesmo mês: mostra data "DD/MM"
      evolucao = days.map(({ key, precoMedio: pm }) => ({
        mes:       `${key.substring(8, 10)}/${key.substring(5, 7)}`,
        mesKey:    key,
        precoMedio: pm,
        pct:       ((pm - precoInicial) / precoInicial) * 100,
      }));
    }

    results.push({
      nome,
      nomeAbrev:    abrev(nome, 35),
      pctAumento,
      precoInicial,
      precoFinal,
      rawItems:     its,
      orderIds:     [...new Set(its.map((i) => i.orderId))],
      evolucao,
    });
  }

  return results.sort((a, b) => b.pctAumento - a.pctAumento).slice(0, top);
}

/* ─── 8. Ranking de Insumos ──────────────────────────────────────────────── */

/**
 * Extrai ranking de insumos a partir dos `itemsPreview` dos pedidos.
 * Conta quantos pedidos distintos contêm cada insumo (por frequência).
 */
export function computeRankingInsumos(pedidos: PedidoCard[], top = 15): InsumoStat[] {
  // Mapa: nome_normalizado → { pedidos que o contêm }
  const map = new Map<string, { pedidos: Set<PedidoCard>; orderIds: Set<string> }>();

  for (const p of pedidos) {
    if (!p.itemsPreview?.length) continue;
    const seen = new Set<string>();
    for (const raw of p.itemsPreview) {
      const nome = normalizeItemLabel(raw);
      if (!nome || seen.has(nome)) continue;
      seen.add(nome);
      if (!map.has(nome)) map.set(nome, { pedidos: new Set(), orderIds: new Set() });
      map.get(nome)!.pedidos.add(p);
      map.get(nome)!.orderIds.add(p.purchaseOrderId);
    }
  }

  return Array.from(map.entries())
    .map(([nome, { pedidos: ps, orderIds }]) => ({
      nome,
      nomeAbrev:  abrev(nome, 35),
      quantidade: ps.size,
      pedidos:    Array.from(ps),
      orderIds:   Array.from(orderIds),
    }))
    .sort((a, b) => b.quantidade - a.quantidade)
    .slice(0, top);
}

/**
 * Extrai ranking de insumos a partir dos OrderItems vindos da API.
 * Agrupa por description normalizada, ordena por valor total.
 */
export function computeRankingInsumosFromItems(items: OrderItem[], top = 15): InsumoStat[] {
  type Acc = {
    valorTotal:  number;
    qtdComprada: number;
    pedidosSet:  Set<string>;
    rawItems:    OrderItem[];
  };
  const map = new Map<string, Acc>();

  for (const it of items) {
    const nome = normalizeItemLabel(it.description || it.code || "Sem descrição");
    if (!nome) continue;
    if (!map.has(nome)) {
      map.set(nome, { valorTotal: 0, qtdComprada: 0, pedidosSet: new Set(), rawItems: [] });
    }
    const acc = map.get(nome)!;
    acc.valorTotal  += it.totalAmount  ?? 0;
    acc.qtdComprada += it.quantity     ?? 0;
    acc.pedidosSet.add(it.orderId);
    acc.rawItems.push(it);
  }

  return Array.from(map.entries())
    .map(([nome, acc]) => ({
      nome,
      nomeAbrev:   abrev(nome, 35),
      quantidade:  acc.pedidosSet.size,
      valorTotal:  acc.valorTotal,
      qtdComprada: acc.qtdComprada,
      pedidos:     [],
      orderIds:    Array.from(acc.pedidosSet),
      rawItems:    acc.rawItems,
    }))
    .sort((a, b) => (b.valorTotal ?? 0) - (a.valorTotal ?? 0))
    .slice(0, top);
}
